import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { pillarArticlesA } from '@/mocks/pillarArticlesA';
import { pillarArticlesB } from '@/mocks/pillarArticlesB';
import ProductSection from '@/components/feature/ProductSection';

function getCategoryName(cat: (typeof categories)[0], lang: string) {
  if (lang === 'pt') return cat.namePt;
  if (lang === 'de') return cat.nameDe;
  return cat.name;
}

function pick(text: { en: string; pt: string; de: string }, lang: string) {
  if (lang === 'pt') return text.pt;
  if (lang === 'de') return text.de;
  return text.en;
}

const pillarArticles = [...pillarArticlesA, ...pillarArticlesB];

export default function Home() {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const lang = i18n.language || 'en';
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const popularResources = [
    {
      key: 'spreadsheet',
      href: `/${lang}/cssbuy-spreadsheet`,
      image:
        'https://readdy.ai/api/search-image?query=A%20clean%20organized%20spreadsheet%20document%20with%20product%20thumbnail%20images%20and%20green%20checkmarks%20on%20a%20bright%20white%20desk%20surface%2C%20soft%20natural%20light%2C%20modern%20productivity%20concept%20photography%20with%20a%20fresh%20minimal%20aesthetic&width=300&height=300&seq=res-spreadsheet&orientation=squarish',
    },
    {
      key: 'review',
      href: `/${lang}/review`,
      image:
        'https://readdy.ai/api/search-image?query=Five%20golden%20star%20rating%20icons%20floating%20above%20an%20open%20laptop%20on%20a%20warm%20cream%20background%2C%20soft%20glowing%20light%2C%20positive%20customer%20review%20concept%20photography%20with%20a%20clean%20modern%20minimal%20aesthetic&width=300&height=300&seq=res-review&orientation=squarish',
    },
    {
      key: 'shipping',
      href: `/${lang}/shipping`,
      image:
        'https://readdy.ai/api/search-image?query=A%20small%20delivery%20van%20and%20stacked%20cardboard%20packages%20on%20a%20soft%20light%20blue%20background%2C%20bright%20even%20lighting%2C%20logistics%20and%20shipping%20concept%20photography%20with%20a%20clean%20modern%20minimal%20aesthetic&width=300&height=300&seq=res-shipping&orientation=squarish',
    },
    {
      key: 'coupon',
      href: `/${lang}/coupon`,
      image:
        'https://readdy.ai/api/search-image?query=A%20pair%20of%20scissors%20cutting%20a%20dotted%20discount%20coupon%20ticket%20on%20a%20warm%20beige%20background%2C%20soft%20natural%20light%2C%20savings%20and%20promotion%20concept%20photography%20with%20a%20fresh%20clean%20minimal%20aesthetic&width=300&height=300&seq=res-coupon&orientation=squarish',
    },
    {
      key: 'discount',
      href: `/${lang}/discount`,
      image:
        'https://readdy.ai/api/search-image?query=A%20bright%20percentage%20discount%20price%20tag%20on%20a%20soft%20coral%20background%2C%20soft%20studio%20lighting%2C%20sale%20and%20promotion%20concept%20photography%20with%20a%20clean%20modern%20minimal%20aesthetic&width=300&height=300&seq=res-discount&orientation=squarish',
    },
    {
      key: 'warehouse',
      href: `/${lang}/warehouse`,
      image:
        'https://readdy.ai/api/search-image?query=Neatly%20stacked%20boxes%20and%20storage%20shelves%20in%20a%20bright%20organized%20warehouse%2C%20soft%20natural%20lighting%2C%20logistics%20storage%20concept%20photography%20with%20a%20clean%20modern%20minimal%20aesthetic&width=300&height=300&seq=res-warehouse&orientation=squarish',
    },
    {
      key: 'legit',
      href: `/${lang}/legit`,
      image:
        'https://readdy.ai/api/search-image?query=A%20glowing%20green%20shield%20with%20a%20checkmark%20symbol%20on%20a%20soft%20white%20background%2C%20trustworthy%20security%20concept%20photography%20with%20a%20bright%20clean%20modern%20minimal%20aesthetic&width=300&height=300&seq=res-legit&orientation=squarish',
    },
    {
      key: 'qc',
      href: `/${lang}/qc`,
      image:
        'https://readdy.ai/api/search-image?query=A%20magnifying%20glass%20inspecting%20a%20premium%20product%20on%20a%20clean%20inspection%20table%2C%20soft%20bright%20lighting%2C%20quality%20control%20concept%20photography%20with%20a%20crisp%20modern%20minimal%20aesthetic&width=300&height=300&seq=res-qc&orientation=squarish',
    },
    {
      key: 'sellers',
      href: `/${lang}/sellers`,
      image:
        'https://readdy.ai/api/search-image?query=A%20modern%20storefront%20with%20a%20glowing%20open%20sign%20and%20neat%20product%20display%20on%20a%20warm%20neutral%20background%2C%20soft%20inviting%20lighting%2C%20trusted%20seller%20concept%20photography%20with%20a%20clean%20modern%20minimal%20aesthetic&width=300&height=300&seq=res-sellers&orientation=squarish',
    },
  ];

  const handleCategoryClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[600px] md:min-h-[720px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20sophisticated%20editorial%20flat%20lay%20of%20premium%20fashion%20products%20including%20white%20sneakers%20a%20folded%20beige%20hoodie%20a%20leather%20handbag%20and%20sunglasses%20arranged%20on%20a%20warm%20cream%20marble%20surface%2C%20soft%20natural%20side%20lighting%20with%20gentle%20shadows%2C%20high-end%20e-commerce%20catalog%20photography%20with%20a%20clean%20airy%20minimalist%20composition&width=1600&height=900&seq=hero-bg&orientation=landscape&nocache=true"
            alt="CSSBuy Spreadsheet Products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/60 to-foreground-950/30" />
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-16 xl:px-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-500/20 text-primary-300 text-sm font-medium mb-6">
              <i className="ri-sparkling-line" />
              <span>2026 Resource Hub</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-6">
              {t('hero.h1')}
            </h1>
            <p className="text-base md:text-lg text-white/80 leading-relaxed mb-8 max-w-2xl">
              {t('hero.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="btn-accent text-base px-8 py-4"
              >
                <i className="ri-shopping-bag-3-line mr-2" />
                {t('buttons.explore')}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="btn-outline text-base px-8 py-4 border-white/30 text-white hover:bg-white/10 hover:border-white/50"
              >
                <i className="ri-external-link-line mr-2" />
                {t('buttons.viewGuides')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Category Dropdown Section */}
      <section className="section-padding bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {t('categories.title')}
            </h2>
            <p className="text-foreground-500 max-w-xl mx-auto">
              {t('categories.subtitle')}
            </p>
          </div>

          <div className="relative max-w-md mx-auto mb-10" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between px-5 py-4 bg-white rounded-xl border border-background-200 hover:border-primary-400 transition-colors text-left"
            >
              <span className="flex items-center gap-2 text-foreground-800">
                <i className="ri-grid-line text-primary-500" />
                <span className="font-medium">{t('categories.title')}</span>
              </span>
              <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-background-200 shadow-xl overflow-hidden z-40 max-h-[320px] overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      handleCategoryClick(cat.url);
                      setDropdownOpen(false);
                    }}
                    className="w-full text-left px-5 py-3 hover:bg-primary-50 transition-colors flex items-center justify-between group"
                  >
                    <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600">
                      {getCategoryName(cat, lang)}
                    </span>
                    <i className="ri-external-link-line text-foreground-400 text-xs group-hover:text-primary-500" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Category Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-4">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryClick(cat.url)}
                className="group relative overflow-hidden rounded-xl bg-white border border-background-200 hover:border-primary-300 transition-all duration-200 hover:translate-y-[-2px] text-left"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={cat.image}
                    alt={getCategoryName(cat, lang)}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="p-3">
                  <p className="text-xs font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors leading-tight">
                    {getCategoryName(cat, lang)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Product Showcase Section */}
      <ProductSection />

      {/* Popular Resources Section */}
      <section className="section-padding bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {t('popularResources.title')}
            </h2>
            <p className="text-foreground-500 max-w-xl mx-auto">
              {t('popularResources.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {popularResources.map((resource) => (
              <button
                key={resource.key}
                onClick={() => navigate(resource.href)}
                className="group flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-200 text-left"
              >
                <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0">
                  <img
                    src={resource.image}
                    alt={t(`popularResources.items.${resource.key}`)}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground-800 group-hover:text-primary-700 transition-colors text-sm">
                    {t(`popularResources.items.${resource.key}`)}
                  </p>
                  <p className="text-xs text-foreground-400 mt-0.5">{t('buttons.readMore')}</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-300 group-hover:text-primary-500 transition-colors shrink-0" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=An%20elegant%20shopping%20lifestyle%20scene%20with%20neatly%20wrapped%20premium%20packages%20and%20gift%20boxes%20in%20warm%20neutral%20tones%20on%20a%20soft%20linen%20surface%2C%20golden%20hour%20lighting%20with%20a%20cozy%20inviting%20atmosphere%2C%20refined%20e-commerce%20photography%20with%20a%20clean%20sophisticated%20minimalist%20aesthetic&width=1600&height=600&seq=cta-bg&orientation=landscape&nocache=true"
            alt="Shop Now"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-foreground-950/70" />
        </div>
        <div className="relative z-10 section-padding text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
              {t('ctaSection.title')}
            </h2>
            <p className="text-white/80 mb-8 leading-relaxed">
              {t('ctaSection.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="btn-accent text-base px-8 py-4"
              >
                <i className="ri-store-2-line mr-2" />
                {t('ctaSection.button1')}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="btn-outline text-base px-8 py-4 border-white/30 text-white hover:bg-white/10 hover:border-white/50"
              >
                <i className="ri-shopping-cart-2-line mr-2" />
                {t('ctaSection.button2')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Guides Section */}
      <section className="section-padding bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {t('home.latestGuides')}
            </h2>
            <p className="text-foreground-500 max-w-xl mx-auto">
              {t('home.latestGuidesSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <button
              onClick={() => navigate(`/${lang}/cssbuy-spreadsheet`)}
              className="group text-left rounded-xl bg-white border border-background-200 overflow-hidden hover:border-primary-300 transition-all duration-200 hover:translate-y-[-2px]"
            >
              <div className="aspect-[3/2] overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20clean%20modern%20laptop%20screen%20showing%20an%20organized%20fashion%20product%20spreadsheet%20with%20thumbnail%20images%20of%20sneakers%20and%20clothing%2C%20bright%20minimal%20desk%20scene%20with%20soft%20daylight%2C%20contemporary%20tech%20workspace%20photography%20with%20a%20fresh%20clean%20aesthetic&width=600&height=400&seq=article-1&orientation=landscape&nocache=true"
                  alt="What Is CSSBuy Spreadsheet"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors mb-2">
                  What Is CSSBuy Spreadsheet?
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                  Complete beginner guide to understanding and using CSSBuy Spreadsheet for finding products and sellers.
                </p>
                <span className="inline-flex items-center text-sm font-medium text-primary-600 group-hover:text-primary-700">
                  {t('buttons.readMore')}
                  <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </button>
            <button
              onClick={() => navigate(`/${lang}/shipping`)}
              className="group text-left rounded-xl bg-white border border-background-200 overflow-hidden hover:border-primary-300 transition-all duration-200 hover:translate-y-[-2px]"
            >
              <div className="aspect-[3/2] overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20stack%20of%20premium%20kraft%20shipping%20boxes%20with%20neat%20packing%20tape%20on%20a%20warm%20concrete%20floor%2C%20soft%20studio%20lighting%20with%20gentle%20shadows%2C%20professional%20e-commerce%20logistics%20photography%20with%20a%20clean%20modern%20minimal%20aesthetic&width=600&height=400&seq=article-2&orientation=landscape&nocache=true"
                  alt="CSSBuy Shipping Cost Guide"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors mb-2">
                  CSSBuy Shipping Cost Guide
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                  Everything about shipping methods, costs, weight calculations, and tips to save money on international orders.
                </p>
                <span className="inline-flex items-center text-sm font-medium text-primary-600 group-hover:text-primary-700">
                  {t('buttons.readMore')}
                  <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </button>
            <button
              onClick={() => navigate(`/${lang}/qc`)}
              className="group text-left rounded-xl bg-white border border-background-200 overflow-hidden hover:border-primary-300 transition-all duration-200 hover:translate-y-[-2px]"
            >
              <div className="aspect-[3/2] overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Close-up%20of%20hands%20inspecting%20a%20premium%20white%20sneaker%20under%20soft%20bright%20light%20on%20a%20clean%20inspection%20table%2C%20professional%20quality%20control%20detail%20photography%20with%20a%20crisp%20modern%20minimal%20aesthetic&width=600&height=400&seq=article-3&orientation=landscape&nocache=true"
                  alt="CSSBuy QC and Quality Check"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors mb-2">
                  CSSBuy QC & Quality Check
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                  Learn how to read QC photos, what to look for, and how to avoid quality issues before shipping.
                </p>
                <span className="inline-flex items-center text-sm font-medium text-primary-600 group-hover:text-primary-700">
                  {t('buttons.readMore')}
                  <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Pillar Articles Section */}
      <section className="section-padding bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {t('home.pillarTitle')}
            </h2>
            <p className="text-foreground-500 max-w-xl mx-auto">
              {t('home.pillarSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pillarArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/${lang}/articles/${article.slug}`}
                className="group text-left rounded-xl bg-white border border-background-200 overflow-hidden hover:border-primary-300 transition-all duration-200 hover:translate-y-[-2px]"
              >
                <div className="aspect-[3/2] overflow-hidden">
                  <img
                    src={article.image}
                    alt={pick(article.title, lang)}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-lg font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors mb-2">
                    {pick(article.title, lang)}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                    {pick(article.summary, lang)}
                  </p>
                  <span className="inline-flex items-center text-sm font-medium text-primary-600 group-hover:text-primary-700">
                    {t('buttons.readMore')}
                    <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / Safety Banner */}
      <section className="section-padding bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="bg-background-100 rounded-2xl p-6 md:p-10 flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center shrink-0">
              <i className="ri-shield-check-line text-3xl text-primary-600" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                Independent Resource Hub
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                This website is an independent informational resource and is not officially affiliated with or endorsed by CSSBuy. We provide unbiased guides, reviews, and resources to help you make informed shopping decisions.
              </p>
            </div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="btn-primary shrink-0"
            >
              {t('buttons.buyNow')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}